import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Button,
  Divider
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  DirectionsWalk as StepsIcon,
  Schedule as TimeIcon,
  Chat as ChatIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
  transition: 'box-shadow 0.3s ease-in-out',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const LeaderboardHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 2),
  color: theme.palette.text.secondary,
}));

const RankChip = styled(Chip)(({ theme, rank }) => {
  let bgcolor, color;
  
  switch (rank) {
    case 1:
      bgcolor = '#FFD700'; // Gold
      color = '#000';
      break;
    case 2:
      bgcolor = '#C0C0C0'; // Silver
      color = '#000';
      break;
    case 3:
      bgcolor = '#CD7F32'; // Bronze
      color = '#fff';
      break;
    default:
      bgcolor = theme.palette.grey[200];
      color = theme.palette.text.primary;
  }

  return {
    backgroundColor: bgcolor,
    color: color,
    fontWeight: 'bold',
    minWidth: 32,
    height: 24,
    fontSize: '0.75rem',
    '& .MuiChip-label': {
      padding: '0 6px',
    }
  };
});

const StepsText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const LastSyncText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const UserListItem = styled(ListItem)(({ theme, isCurrentUser }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(0.5),
  backgroundColor: isCurrentUser ? theme.palette.action.selected : 'transparent',
  border: isCurrentUser ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

function FriendsLeaderboard({ 
  leaderboard = [],
  loading = false,
  onChatClick
}) {
  const formatLastSync = (lastSyncString) => {
    if (!lastSyncString) return 'Never synced';
    
    const lastSync = new Date(lastSyncString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - lastSync) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatSteps = (steps) => {
    return steps.toLocaleString();
  };

  if (loading) {
    return (
      <StyledCard>
        <CardContent>
          <HeaderBox>
            <LeaderboardHeader variant="h6">
              <TrophyIcon color="primary" />
              TODAY'S TOP FRIENDS
            </LeaderboardHeader>
          </HeaderBox>
          <Typography>Loading...</Typography>
        </CardContent>
      </StyledCard>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return (
      <StyledCard>
        <CardContent>
          <HeaderBox>
            <LeaderboardHeader variant="h6">
              <TrophyIcon color="primary" />
              TODAY'S TOP FRIENDS
            </LeaderboardHeader>
          </HeaderBox>
          <EmptyStateBox>
            <PersonIcon 
              sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} 
            />
            <Typography variant="body1" gutterBottom>
              Looks like none of your friends have synced yet today
            </Typography>
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              onClick={onChatClick}
              sx={{ mt: 2 }}
            >
              Go to chat
            </Button>
          </EmptyStateBox>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardContent>
        <HeaderBox>
          <LeaderboardHeader variant="h6">
            <TrophyIcon color="primary" />
            TODAY'S TOP FRIENDS
          </LeaderboardHeader>
        </HeaderBox>

        <List disablePadding>
          {leaderboard.map((friend, index) => (
            <React.Fragment key={friend.user_id}>
              <UserListItem isCurrentUser={friend.is_current_user}>
                <ListItemAvatar>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={friend.avatar}
                      sx={{ 
                        bgcolor: friend.is_current_user ? 'primary.main' : 'grey.400',
                        width: 40,
                        height: 40
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <RankChip
                      rank={friend.rank}
                      label={friend.rank}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        zIndex: 1
                      }}
                    />
                  </Box>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: friend.is_current_user ? 'bold' : 'medium',
                          color: friend.is_current_user ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {friend.display_name}
                        {friend.is_current_user && ' (You)'}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <StepsText variant="body2">
                        <StepsIcon fontSize="small" />
                        {formatSteps(friend.steps)} steps
                      </StepsText>
                      <LastSyncText>
                        <TimeIcon fontSize="small" />
                        {formatLastSync(friend.last_sync)}
                      </LastSyncText>
                    </Box>
                  }
                />
              </UserListItem>
              {index < leaderboard.length - 1 && (
                <Divider variant="inset" component="li" />
              )}
            </React.Fragment>
          ))}
        </List>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Updates throughout the day as friends sync their devices
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
}

export default FriendsLeaderboard;